<template>
    <div class="pillar-table-wrapper">
        <div class="table-header">
            <h5 class="table-title">Resumen por elemento</h5>
        </div>
        <div class="card p-0">
            <div class="card-body p-0">
                <div class="table-wrapper">
                    <table class="table table-sm mb-0">
                        <thead class="thead-light">
                            <tr>
                                <th class="align-middle sticky-column">Elemento</th>
                                <th class="text-center rating-header">Promedio</th>
                                <th 
                                    v-for="rating in ratingColumns" 
                                    :key="'header-' + rating.id"
                                    class="text-center rating-header"
                                >
                                    {{ rating.name }}
                                </th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr v-for="(element, index) in flatElements" :key="`element-${index}`" class="element-row">
                                <td class="element-name sticky-column" :style="{ backgroundColor: element.color || '#e9ecef' }">
                                    {{ element.name }}
                                </td>
                                <td class="text-center average-cell">
                                    <strong>{{ calculateAverage(element.ratings) }}</strong>
                                </td>
                                <td 
                                    class="text-center percentage-cell" 
                                    v-for="rating in ratingColumns" 
                                    :key="'rating-cell-' + rating.id"
                                >
                                    <div class="percentage-content">
                                        <span class="percentage-text">
                                            {{ getRatingPercentage(element.ratings, rating).toFixed(2) }}%
                                        </span>
                                        <div class="progress-bar-wrapper">
                                            <div class="progress-bar" 
                                                 :style="{ 
                                                     width: getRatingPercentage(element.ratings, rating) + '%',
                                                     backgroundColor: getRatingColor(rating)
                                                 }">
                                            </div>
                                        </div>
                                    </div>
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
export default {
    name: 'PillarTable',
    props: {
        tableData: {
            type: Array,
            default: () => []
        },
        ratingDefinitions: {
            type: Array,
            default: () => []
        }
    },
    computed: {
        flatElements() {
            if (!this.tableData || !Array.isArray(this.tableData)) {
                return [];
            }
            
            const elements = [];
            this.tableData.forEach(pillar => {
                if (pillar.elements && Array.isArray(pillar.elements)) {
                    pillar.elements.forEach(element => {
                        elements.push({
                            ...element,
                            pillarName: pillar.name
                        });
                    });
                }
            });
            
            return elements;
        },
        ratingColumns() {
            if (this.ratingDefinitions && this.ratingDefinitions.length) {
                return this.ratingDefinitions.map((rating, index) => ({
                    ...rating,
                    lookupKey: rating.id,
                    fallbackKey: `rating_${index}`
                }));
            }

            const defaults = [
                { id: 'legacy-0', name: 'No aplica', value: 0, color: '#6c757d' },
                { id: 'legacy-1', name: 'Muy bajo', value: 1, color: '#dc3545' },
                { id: 'legacy-2', name: 'Bajo', value: 2, color: '#fd7e14' },
                { id: 'legacy-3', name: 'Medio', value: 3, color: '#ffc107' },
                { id: 'legacy-4', name: 'Alto', value: 4, color: '#17a2b8' },
                { id: 'legacy-5', name: 'Muy alto', value: 5, color: '#28a745' }
            ];

            return defaults.map((rating, index) => ({
                ...rating,
                lookupKey: `rating_${index}`,
                fallbackKey: `rating_${index}`
            }));
        }
    },
    methods: {
        calculateAverage(ratings) {
            if (!ratings || !ratings.total) return '0.00';

            let sum = 0;
            const stats = ratings.ratings || null;

            this.ratingColumns.forEach((rating, index) => {
                const entry = this.getRatingEntry(ratings, rating, index);
                if (entry) {
                    sum += entry.count * (typeof rating.value === 'number' ? rating.value : index);
                }
            });

            return (sum / ratings.total).toFixed(2);
        },
        getRatingPercentage(ratings, rating, index) {
            if (!ratings) return 0;
            const entry = this.getRatingEntry(ratings, rating, index);
            return entry ? (entry.percentage || 0) : 0;
        },
        getRatingEntry(ratings, rating, index = 0) {
            if (!ratings) return null;
            const stats = ratings.ratings;

            if (stats && Object.prototype.hasOwnProperty.call(stats, rating.lookupKey)) {
                return stats[rating.lookupKey];
            }

            const legacyKey = rating.fallbackKey || rating.lookupKey || `rating_${index}`;
            if (ratings[legacyKey]) {
                return ratings[legacyKey];
            }

            return null;
        },
        getRatingColor(rating) {
            return rating.color || '#9CA3AF';
        }
    }
};
</script>

<style scoped>
.pillar-table-wrapper {
    width: 100%;
    background-color: transparent;
    max-height: 550px;
    display: flex;
    flex-direction: column;
}

.table-header {
    display: flex;
    align-items: center;
    margin-bottom: 0.75rem;
}

.table-title {
    margin: 0;
    font-size: 18px;
    font-weight: 600;
    color: #1B212E;
}

.card {
    border: 1px solid #e2e8f0;
    border-radius: 25px;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
    background-color: #ffffff;
    overflow: hidden;
    flex: 1;
    display: flex;
    flex-direction: column;
    min-height: 0;
}

.card-body {
    flex: 1;
    display: flex;
    flex-direction: column;
    min-height: 0;
    padding: 0 !important;
}

/* Wrapper con scroll */
.table-wrapper {
    flex: 1;
    overflow-y: auto;
    overflow-x: auto;
    position: relative;
    min-height: 0;
}

.table {
    margin-bottom: 0;
    font-size: 13px;
    border-collapse: separate;
    border-spacing: 0;
}

/* Header de la tabla - Fijo y sutil */
.table thead th {
    background-color: #f8f9fa;
    color: #495057;
    font-weight: 600;
    font-size: 11px;
    padding: 10px 6px;
    border-bottom: 2px solid #dee2e6;
    border-top: none;
    border-left: none;
    border-right: 1px solid #e9ecef;
    vertical-align: middle;
    position: sticky;
    top: 0;
    z-index: 10;
}

.rating-header {
    min-width: 80px;
}

.table thead th:last-child {
    border-right: none;
}

/* Filas de elementos */
.element-row td {
    padding: 10px 8px;
    border-bottom: 1px solid #f0f0f0;
    border-top: none;
    border-left: none;
    border-right: 1px solid #f0f0f0;
    vertical-align: middle;
}

.element-row td:last-child {
    border-right: none;
}

.element-row:hover td:not(.element-name) {
    background-color: #f8f9fa;
}

.element-row:hover .element-name {
    opacity: 0.95;
}

/* Columna sticky (fija) */
.sticky-column {
    position: sticky;
    left: 0;
    z-index: 5;
    box-shadow: 2px 0 4px rgba(0, 0, 0, 0.05);
}

.table thead th.sticky-column {
    z-index: 15;
    background-color: #f8f9fa;
}

/* Nombre del elemento */
.element-name {
    color: #1B212E;
    font-weight: 600;
    text-align: left;
    padding-left: 12px !important;
    min-width: 250px;
    max-width: 350px;
}

/* Celda de promedio */
.average-cell {
    font-weight: 700;
    color: #1B212E;
    font-size: 14px;
    min-width: 80px;
}

/* Celdas de porcentaje */
.percentage-cell {
    min-width: 80px;
    padding: 6px !important;
}

.percentage-content {
    display: flex;
    flex-direction: column;
    gap: 4px;
}

.percentage-text {
    font-weight: 600;
    font-size: 12px;
    color: #1B212E;
}

/* Barras de progreso */
.progress-bar-wrapper {
    width: 100%;
    height: 8px;
    background-color: #e9ecef;
    border-radius: 4px;
    overflow: hidden;
}

.progress-bar {
    height: 100%;
    border-radius: 4px;
    transition: width 0.3s ease, background-color 0.3s ease;
}

/* Scrollbar personalizado */
.table-wrapper::-webkit-scrollbar {
    width: 6px;
    height: 6px;
}

.table-wrapper::-webkit-scrollbar-track {
    background: transparent;
}

.table-wrapper::-webkit-scrollbar-thumb {
    background: #a0aec0;
    border-radius: 10px;
}

.table-wrapper::-webkit-scrollbar-thumb:hover {
    background: #718096;
}

.table-wrapper::-webkit-scrollbar-corner {
    background: transparent;
}

@media (max-width: 767px) {
    .table-title {
        font-size: 14px;
    }
    
    .table {
        font-size: 10px;
    }
    
    .table thead th {
        font-size: 9px;
        padding: 6px 4px;
    }
    
    .element-row td {
        padding: 6px 4px;
    }
    
    .element-name {
        font-size: 10px;
        min-width: 150px;
        padding-left: 8px !important;
    }
    
    .value-cell {
        font-size: 10px;
    }
    
    .percentage-text {
        font-size: 9px;
    }
    
    .progress-bar-wrapper {
        height: 6px;
    }
    
    .card-body {
        padding: 0.5rem !important;
    }
}
</style>
